import React, { FC, useEffect, ReactNode } from "react";
import classNames from "classnames";
import { useSelect, UseSelectProps } from "downshift";

import { FieldStatus } from "../../Helpers/FieldStatus";

import { ServiceDto } from "../../../../../Api/Model/ServiceDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { DoctorDto } from "../../../../../Api/Model/DoctorDto.csharp";
import { FirstStepBookingLabelsDto } from "../../../../../Api/Model/BookingSteps/FirstStepBookingLabelsDto.csharp";
import { interpolate } from "../../Helpers";
import { FieldErrorPosition } from "../../Helpers/interfaces";
import { ServiceGroupDto } from "@apiModels/ServiceGroupDto.csharp";

type ServiceOrGroupSelectProps = {
  services: UseSelectProps<ServiceGroupDto | ServiceDto | null>;
  label: string;
  placeholder: string;
  onSelect: (serviceOrGroup: ServiceGroupDto | ServiceDto) => void;
  unavailableServices: {
    items: (ServiceGroupDto | ServiceDto)[];
  };
  status?: FieldStatus;
  error?: {
    message?: string;
  };
  dependencies?: string[];
  defaultValue?: ServiceGroupDto | ServiceDto | null;
  location: LocationDto;
  doctor: DoctorDto;
  additionalLabels: FirstStepBookingLabelsDto;
  errorPosition?: FieldErrorPosition;
};

export const ServiceOrGroupSelect: FC<ServiceOrGroupSelectProps> = ({
  services,
  label,
  placeholder,
  onSelect,
  unavailableServices = {
    items: [],
  },
  status = FieldStatus.active,
  error,
  dependencies = [],
  defaultValue = undefined,
  location,
  doctor,
  additionalLabels,
  errorPosition = FieldErrorPosition.left,
}) => {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useSelect(services);

  const id = `input-service-or-group`;

  useEffect(() => {
    if (services.items.length == 1) {
      if (selectedItem && services.items[0]!.id === selectedItem.id) return;
      selectItem(services.items[0]);
      onSelect(services.items[0]!);
    }
  }, [services.items]);

  useEffect(() => {
    if (selectedItem != null && selectedItem.id) {
      onSelect(selectedItem);
    }
  }, [selectedItem]);

  useEffect(() => {
    selectItem(null);
  }, [...dependencies]);

  useEffect(() => {
    if (defaultValue?.id) {
      selectItem(defaultValue);
    }
  }, []);

  function getUnavailableServicesSubheading() {
    if (doctor.id && doctor.id != "-1")
      return interpolate(
        additionalLabels.serviceNotByDoctor,
        "$doctor",
        doctor.firstName
      );
    if (location.id && location.id != "-1") {
      return location.isLocation
        ? interpolate(
            additionalLabels.serviceNotByLocation,
            "$location",
            location.name
          )
        : interpolate(
            additionalLabels.serviceNotByCenter,
            "$center",
            location.name
          );
    }
    return "";
  }

  const mapService = (
    item: ServiceGroupDto | ServiceDto | null,
    index: number,
    selectable: boolean
  ): ReactNode => {
    const itemProps = selectable ? getItemProps({ item, index }) : {};
    return (
      <li
        key={item!.id}
        className={classNames("select__option p-1", {
          "select__option--highlight": selectable && highlightedIndex === index,
        })}
        {...itemProps}
      >
        <p className="m-0">
          <strong>{getName(item!)}</strong>
        </p>
        <p className="m-0">{item!.description}</p>
      </li>
    );
  };

  function instanceOfServiceDto(
    serviceOrGroup: ServiceDto | ServiceGroupDto
  ): serviceOrGroup is ServiceDto {
    return "bookingGroupName" in serviceOrGroup;
  }

  const getName = (serviceOrGroup: ServiceDto | ServiceGroupDto) => {
    return (
      <>
        {instanceOfServiceDto(serviceOrGroup) &&
        !!serviceOrGroup.bookingGroupName
          ? serviceOrGroup.bookingGroupName
          : serviceOrGroup.bookingName}
      </>
    );
  };

  const madeSelection =
    selectedItem != null || (defaultValue && defaultValue.id);

  return (
    <div
      className={classNames(
        "position-relative my-2 select booking-narrow-item",
        {
          "select--disabled": status == FieldStatus.disabled,
          "select--error": status == FieldStatus.error,
        }
      )}
    >
      <button
        className="ps-1 py-1 pe-3 select__field"
        type="button"
        {...getToggleButtonProps()}
        disabled={status == FieldStatus.disabled}
      >
        {(selectedItem && getName(selectedItem)) ??
          defaultValue?.bookingName ??
          ""}
      </button>
      <span
        {...getLabelProps()}
        className={classNames("position-absolute select__label", {
          "select__label--focus": madeSelection,
        })}
      >
        {madeSelection && label}
        {!madeSelection && placeholder}
      </span>
      <ul className="select__optionsList" {...getMenuProps()}>
        {isOpen && (
          <>
            {services.items.map((s, i) => mapService(s, i, true))}
            {unavailableServices.items.length > 0 && (
              <>
                <li className="select__option p-1 pt-2 select__optionGroupHeader unselectable-options-below border-top border-4 border-info">
                  {getUnavailableServicesSubheading()}
                </li>
                {unavailableServices.items.map((s, i) =>
                  mapService(s, i, false)
                )}
              </>
            )}
          </>
        )}
      </ul>
      {status == FieldStatus.error && error && error.message && (
        <div
          className={classNames("input__support p-1", "input__support--error", {
            "input__support--errorLeft":
              errorPosition == FieldErrorPosition.left,
            "input__support--errorBottom":
              errorPosition == FieldErrorPosition.bottom,
          })}
        >
          <p id={"errorId-" + id} className="fs-6 input__supportText">
            {error.message}
          </p>
        </div>
      )}
    </div>
  );
};
